import { Scale } from "phaser";
import Button from "../core/Button";
import Strings from "../core/data/Strings";
import { GameObject } from "../core/GameObject";
import ScaleManager from "../core/ScaleManager";
import GameColors from "../GameColors";
import PlayerData from "../PlayerData";
import { GameScene } from "../scenes/GameScene";
import Fonts from "../utils/Fonts";
import LayoutUtils from "../utils/LayoutUtils";
import Overlay from "../utils/Overlay";

export class MissionsPopup extends GameObject {

	scene: GameScene;
	static ON_CLOSED = "onClosed";
	resize: () => void;
	textContainer: Phaser.GameObjects.Container;
	charUnlockText: Phaser.GameObjects.Text;
	charUnlockBG: Phaser.GameObjects.Image;
	charUnlockContainer: Phaser.GameObjects.Container;
	uiButtons: (Phaser.GameObjects.Container | Button)[]=[];
	meters: Phaser.GameObjects.Image[] = [];

	glows: Phaser.GameObjects.Image[];
	backerContainer: Phaser.GameObjects.Container;

	charBacker: Phaser.GameObjects.Image;
	headerText: Phaser.GameObjects.Text;

	constructor(scene: GameScene, buildDelay: number) {
		super(scene);
		const saveData = PlayerData.Instance.saveData;

		const overlay = this.addImage("missionsBG");
		ScaleManager.ScaleBackgroundFromZone(overlay, scene.viewport);
		overlay.setInteractive();

		const starBurstSpine = scene.add.spine(0, 0, "star-burst", "starburst-idle", true);
        const starBurst = scene.add.spineContainer(0, 0, starBurstSpine);
        this.add(starBurst);

		const popup = new GameObject(scene);
		this.add(popup);

		this.glows = [];
		const leftGlow = this.addImage("shopBGGlow");
        const rightGlow = this.addImage("shopBGGlow");
		popup.add([leftGlow, rightGlow]);
		this.glows.push(leftGlow, rightGlow);
		for (const glow of this.glows) {
			glow.setScale(0);
			glow.setAlpha(0);
		}

		this.backerContainer = scene.add.container(0, 0);
		popup.add(this.backerContainer);
		const backer = this.addImage("missionsBacker");
		this.backerContainer.add(backer);

		this.textContainer = scene.add.container(0,0);
		popup.add(this.textContainer);

		const mashupText = scene.add.text(0, 0, Strings.get("MashupParty").toUpperCase(), {
			fontFamily: Fonts.MaliBoldItalic,
			color: "#352E29",
			fontSize: "45px",
		});
		LayoutUtils.scaleToMaxWidth(mashupText, 450);
		mashupText.setOrigin(0.5);
		this.textContainer.add(mashupText);
		mashupText.rotation = 6.17;
		scene.alignIn.Center(mashupText, backer, -20, -380);

		const header = this.headerText = scene.add.text(0, 0, Strings.get("Missions"), {
			fontFamily: Fonts.LuckiestGuy,
			color: GameColors.WHITE,
			align: "center",
			stroke: "#000000",
			strokeThickness: 5,
			fontSize: "80px",
		});
		header.setOrigin(0.5, 0.5);
		this.textContainer.add(header);
		scene.alignIn.TopCenter(header, backer, 0, -245);
		header.setScale(0);

		const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
			fontFamily: Fonts.MaliMediumItalic,
			color: "#352E9",
			fontSize: "32px",
			wordWrap: { width: 500, useAdvancedWrap: true },
			align: "center"
		};

		for (let i = 0; i < 3; i++) {
			const mission = PlayerData.Instance.saveData.missions[i];
			let goalStr = mission.goal.toLocaleString("en-US");
			const clampedProgress = Phaser.Math.Clamp(mission.progress, 0, mission.goal);
			let progressStr = clampedProgress.toLocaleString("en-US");
			const box = popup.addImage("whiteSquare");
			box.setSize(1000, 120);
			box.setDisplaySize(1000, 120);
			box.tint = 0x888888;
			box.alpha = 0.0001;
			scene.alignIn.TopCenter(box, backer, 0, -400 - 120 * i);
			this.backerContainer.add(box);
			let str = Strings.get(mission.type).replace("%N", goalStr + "");
			if (mission.type == "reachEnvironmentIndex") {
				str = Strings.get("reachEnvironment" + mission.goal);
				const envGoal = 1;
				const envProgress = 0;
				goalStr = envGoal.toLocaleString("en-US");
				progressStr = envProgress.toLocaleString("en-US");
			}

			const missionText = scene.add.text(0, 0, str.toUpperCase(), {
				fontFamily: Fonts.MaliMediumItalic,
				color: "#FFEDDF",
				fontSize: "32px",
				align: "left"
			});
			this.textContainer.add(missionText);
			LayoutUtils.scaleToMaxWidth(missionText, 640);
			scene.alignIn.TopLeft(missionText, box, -95, -5);

			const meter = popup.addImage("meterEmpty");
			scene.alignIn.BottomCenter(meter, box, 0, -30);
			this.backerContainer.add(meter);

			let progress = mission.progress / mission.goal;
			if (mission.type == "reachEnvironmentIndex") progress = 0;
			const maxWidth = meter.width - 12
			this.scene.previousMissionProgress.push(clampedProgress);
			this.scene.currentMissionProgress.push({
				type: mission.type,
				amt: mission.progress,
				goal: mission.goal,
				completed: mission.completed
			});
			const greenFill = scene.add.nineSlicedSprite(0, 0, "meterGreenFill", progress < 1 ? progress * maxWidth : maxWidth, 28, 5, 5, 5, 5);
			this.backerContainer.add(greenFill);
			greenFill.setOrigin(0.5);
			scene.alignIn.LeftCenter(greenFill, meter, -6, 0);
			this.meters.push(meter, greenFill);
			const progressText = scene.add.text(0, 0, `${progressStr}/${goalStr}`, {
				fontFamily: Fonts.LuckiestGuy,
				color: "#FB9819",
				fontSize: "32px",
			});
			this.textContainer.add(progressText);
			scene.alignTo.LeftCenter(progressText, meter, 30, 0);

			const rewardBacker = popup.addImage("coinPriceBacker");
			scene.alignIn.RightCenter(rewardBacker, box, -50, 5);
			this.textContainer.add(rewardBacker);
			
			const coin = popup.addImage("missionCoin").setScale(0.8);
			scene.alignIn.LeftCenter(coin, rewardBacker, -10, 0);
			this.textContainer.add(coin);

			const rewardText = scene.add.text(0, 0, mission.reward.toLocaleString("en-US") + "", {
				fontFamily: Fonts.LuckiestGuy,
				color: "#FBF7E7",
				fontSize: "55px",
			});
			this.textContainer.add(rewardText);
			rewardText.scale = Math.min(98 / rewardText.width, 1);
			rewardText.setSize(rewardText.displayWidth, rewardText.displayHeight);
			scene.alignTo.RightCenter(rewardText, coin, 5, -3);
		}
		this.charUnlockContainer = this.scene.add.container(0, 0);
		popup.add(this.charUnlockContainer);

		if (saveData.missionsToNextCharacterUnlock != -1) {
			const charUnlockBG = this.charUnlockBG = popup.addImage("nextCharBacker");
			this.charUnlockContainer.add(charUnlockBG);

			scene.alignIn.BottomCenter(charUnlockBG, backer, 0, -40);
			const charUnlockIcon = popup.addImage("nextCharImg");
			scene.alignIn.LeftCenter(charUnlockIcon, charUnlockBG, -20, -5);
			this.charUnlockContainer.add(charUnlockIcon);

			const missionsLeft = saveData.missionsToNextCharacterUnlock - saveData.missionsCompleteForCharacterUnlock;
			const unlockStr = Strings.get("NextCharacterUnlockedIn").replace("%N", missionsLeft + "").toUpperCase();
			
			const charUnlockText = this.charUnlockText = scene.add.text(0, 0, unlockStr, textStyle);
			this.charUnlockContainer.add(charUnlockText);
			scene.alignIn.Center(charUnlockText, charUnlockBG, 100, -30);
			charUnlockText.rotation = 0.025;

			const meter = popup.addImage("meterEmpty");
			meter.rotation = 0.025;
			scene.alignIn.BottomCenter(meter, charUnlockBG, 100, -45);
			this.charUnlockContainer.add(meter);

			const fill = popup.addImage("meterRedFill");
			fill.setOrigin(0, 0.5);
			scene.alignIn.LeftCenter(fill, meter, -6, -5);
			fill.rotation = 0.025;
			this.charUnlockContainer.add(fill);

			const progress = saveData.missionsCompleteForCharacterUnlock / saveData.missionsToNextCharacterUnlock;
			fill.setScale(Phaser.Math.Clamp(progress, 0, 1), 1);	
		}

		

		const nextContainer = scene.add.container();
		this.add(nextContainer);
		const nextButton = scene.add.button(0, 0, "missionsNextBtn", undefined, this.play, this, true);
		nextContainer.setSize(nextButton.width, nextButton.height);
		nextContainer.add(nextButton);
		this.uiButtons.push(nextContainer);

		const nextText = scene.add.text(0, 0, Strings.get("Action").toUpperCase(), {
			fontFamily: Fonts.LuckiestGuy,
			color: "#FFEDDF",
			fontSize: "38px",
		});
		nextContainer.add(nextText);
		// scene.alignIn.BottomRight(nextButton, overlay, 20, -20);
		// scene.alignIn.TopCenter(nextText, nextButton, 0, 50);

		const tutHand = this.addImage("tutHand");
		tutHand.setOrigin(10 / tutHand.width, 100 / tutHand.height);
		tutHand.angle = 20;
		tutHand.setFlipX(true);
		tutHand.setVisible(false);
		nextContainer.add(tutHand);

		const backButton = scene.add.button(0, 0, "missionsBackBtn", undefined, this.back, this, true);
		this.add(backButton);
		// scene.alignIn.TopLeft(backButton, overlay, 34, -34);
		this.uiButtons.push(backButton);
		for(const button of this.uiButtons) button.scale = 0;
		this.resize = () => {
			overlay.setSize(scene.viewport.width, scene.viewport.height);
			//scene.alignIn.Center(this.textContainer, scene.viewport);
			overlay.setDisplaySize(scene.viewport.width, scene.viewport.height);
			const scale = Math.min(scene.viewport.width / (backer.width + 450), 1);
			scene.alignIn.LeftCenter(leftGlow, backer, leftGlow.width/3, 0);
			scene.alignIn.RightCenter(rightGlow, backer, rightGlow.width/3, 0);
			popup.scale = scale * 0.85;
			scene.alignIn.BottomRight(nextContainer, overlay, -20, -20);
			scene.alignIn.TopCenter(nextText, nextButton, 0, 50);
			scene.alignIn.TopLeft(backButton, overlay, -34, -34);
			if(this.charUnlockText) scene.alignIn.Center(this.charUnlockText, this.charUnlockBG, 100, -25);
			if(this.charUnlockBG)scene.alignIn.BottomCenter(this.charUnlockBG, backer, 0, -40);
			scene.alignIn.Center(tutHand, nextButton, -150, -100);
		};

		scene.addResize(this.resize);
		scene.resize();

		this.textContainer.alpha = 0;
		if (this.charUnlockText) this.charUnlockText.alpha = 0;
		this.backerContainer.y += 1200;
		this.textContainer.y += 1200;

		this.backerContainer.angle = -20;
		this.textContainer.angle = -20;
		for (const meter of this.meters) meter.alpha = 0;

		this.charUnlockContainer.y += 1000;
		setTimeout(() => {
			this.build();
		}, buildDelay);

		setTimeout(() => {
			if (this.scene && tutHand && !PlayerData.Instance.saveData.launchedSlingshot) {
				tutHand.setVisible(true);
				this.scene.tweens.add({
					targets: tutHand,
					scale: 1.2,
					ease: Phaser.Math.Easing.Quadratic.Out,
					yoyo: true,
					repeat: -1,
					duration: 500,
				});
			}
		}, 4750);
	}

	build(): void{
		this.scene.tweens.add({
			targets: [this.backerContainer, this.textContainer],
			y: {from: this.backerContainer.y, to: this.backerContainer.y - 1200},
			angle: 0,
			duration: 500,
			ease: Phaser.Math.Easing.Back.Out,
		});

		this.scene.tweens.add({
			targets: [this.textContainer, this.charUnlockText],
			alpha: 1,
			duration: 200,
			delay: 100,
			ease: Phaser.Math.Easing.Linear,
			onStart: () => {
				this.scene.tweens.add({
					targets: this.headerText,
					scale: 1,
					duration: 200,
					ease: Phaser.Math.Easing.Back.Out,
				});

				for (const meter of this.meters) {
					this.scene.tweens.add({
						targets: meter,
						alpha: 1,
						duration: 300,
						ease: Phaser.Math.Easing.Back.Out,
					});
				}
			}
		});

		this.scene.tweens.add({
			targets: this.charUnlockContainer,
			y: {from: this.charUnlockContainer.y, to: this.charUnlockContainer.y - 1000},
			duration: 300,
			delay: 200,
			ease: Phaser.Math.Easing.Back.Out,
			onStart: () => {
				for(const glow of this.glows){
					this.scene.tweens.add({
						targets: glow,
						scale: 1,
						alpha: 1,
						duration: 300,
						delay: 200,
						ease: Phaser.Math.Easing.Quadratic.Out
					});
				}
			},
		});
		for(const button of this.uiButtons){
			this.scene.tweens.add({
				targets: button,
				scale: 1,
				duration: 300,
				delay: 200,
				ease: Phaser.Math.Easing.Back.Out
			});
		}
	}

	destroy(): void {
		this.scene.removeResize(this.resize);
		super.destroy();
	}

	back(): void{
		this.scene.gotoSplash();
	}

	play(): void {
		this.scene.resumePhaserSystems();
		this.emit(MissionsPopup.ON_CLOSED);
		this.destroy();
	}

}